import {ApplicationConfig, importProvidersFrom, inject, LOCALE_ID, provideZoneChangeDetection} from '@angular/core';
import {ExtraOptions, provideRouter, withRouterConfig} from '@angular/router';

import {appRoutes} from './app-routing.module';
import {
    MatPaginatorTranslation,
    YASC_REST_CONFIG,
    YASC_SIGNALR_SETTINGS,
    YascLibSettings,
    YascRestApiService,
    YascRuntimeCacheService,
    YascSignalRSettings
} from '@yasc/yasc-material-components';
import {environment} from '../environments/environment';
import {
    DateAdapter,
    ErrorStateMatcher,
    MAT_DATE_FORMATS,
    MAT_DATE_LOCALE,
    ShowOnDirtyErrorStateMatcher
} from '@angular/material/core';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {AppInterceptor} from './core/services/http/http.interceptor';
import {RuntimeCacheService} from './core/services/runtime-cache.service';
import {MatPaginatorIntl} from '@angular/material/paginator';
import {RestApiService} from './core/services/rest-api.service';
import {L10nIntlModule, L10nTranslationModule, L10nTranslationService} from 'angular-l10n';
import {MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig} from '@angular/material/dialog';
import {l10nConfig} from '../l10n-config';
import {UserLanguageResolver} from '../languages/UserLanguageResolver';
import {SharedModule} from './core/shared.module';
import {LayoutModule} from '@angular/cdk/layout';
import {DatePipe} from '@angular/common';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {provideMomentDateAdapter} from '@angular/material-moment-adapter';
import {MondayDateAdapter} from './core/services/adapters/monday-date-adapter';


export class GlobalRouteConfig {
    static previousUrl: string = "";
}

const yascLibSettings = new YascLibSettings({
    dateFormat: environment.dateFormat,
    dateFormatShort: environment.dateFormatShort,
    dateFormatInternational: environment.dateFormatInternational,
    dateFormatInternationalWithFractions: environment.dateFormatInternationalWithFractions,
    dateFormatWithFractions: environment.dateFormatWithFractions,
    dateFormatInternationalShort: environment.dateFormatShort,
    restHost: environment.restUrl,
    spinnerEnabled: true,
});

const routerOptions: ExtraOptions = {
    scrollPositionRestoration: 'enabled',
    anchorScrolling: 'enabled',
    scrollOffset: [0, 64],
};

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({eventCoalescing: true}),
        provideRouter(appRoutes, withRouterConfig(routerOptions)),
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimationsAsync(),
        provideMomentDateAdapter({
            parse: {
                dateInput: [yascLibSettings.dateFormatShort],
            },
            display: {
                dateInput: yascLibSettings.dateFormatShort,
                monthYearLabel: 'MMM YYYY',
                dateA11yLabel: 'LL',
                monthYearA11yLabel: 'MMMM YYYY',
            },
        }),
        importProvidersFrom(
            SharedModule,
            LayoutModule,
            DatePipe,
            L10nTranslationModule.forRoot(l10nConfig, {
                localeResolver: UserLanguageResolver,
            }),
            L10nIntlModule,
        ),
        {
            provide: YASC_SIGNALR_SETTINGS,
            useFactory: (): YascSignalRSettings => {
                const runtimeCacheService = inject(RuntimeCacheService);

                return new YascSignalRSettings({
                    socketHost: environment.socketHost,
                    socketLogLevel: environment.socketLogLevel,
                    tokenFactory: () => runtimeCacheService.token,
                    dispatchMethodName: 'dispatch',
                    joinTopicMethodName: 'JoinTopic',
                    leaveTopicMethodName: 'LeaveTopic',
                    publishMethodName: 'Publish',
                    reconnectInterval: [
                        10 * 1000,
                        20 * 1000,
                        40 * 1000,
                        80 * 1000,
                        160 * 1000,
                        300 * 1000
                    ],
                });
            },
        },
        {provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher},
        {
            provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: {
                ...new MatDialogConfig(),
                disableClose: true,
                minWidth: '60vw',
                height: 400
            },
            multi: true,
        },
        {provide: LOCALE_ID, useValue: "de-DE"},
        {provide: MAT_DATE_LOCALE, useValue: 'de-DE'},
        {provide: DateAdapter, useClass: MondayDateAdapter},
        {
            provide: MAT_DATE_FORMATS, useValue: {
                parse: {
                    dateInput: [yascLibSettings.dateFormatShort],
                },
                display: {
                    dateInput: yascLibSettings.dateFormatShort,
                    monthYearLabel: 'MMM YYYY',
                    dateA11yLabel: 'LL',
                    monthYearA11yLabel: 'MMMM YYYY',
                },
            }
        },
        {provide: YASC_REST_CONFIG, useValue: yascLibSettings},
        {provide: YascLibSettings, useValue: yascLibSettings},
        {provide: DatePipe, useClass: DatePipe},
        {provide: HTTP_INTERCEPTORS, useClass: AppInterceptor, multi: true},
        {provide: YascRuntimeCacheService, useClass: RuntimeCacheService},
        {provide: YascRestApiService, useClass: RestApiService},
        {provide: MatPaginatorIntl, useFactory: MatPaginatorTranslation, deps: [L10nTranslationService]},
    ]
};
